import { useContext, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { DetailedIconMovingTruck } from '@zillow/constellation';
import { Heading, List, Modal, Paragraph } from '@zillow/constellation-10';

import { RMWContext } from '../../../providers/RMWContext';

import { RoutingSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import ClaimActions from '../../../actions/ClaimActions';

import PropertiesActions from '../../../actions/PropertiesActions';

import ErrorMessageConstants from '../../../constants/ErrorMessageConstants';

import ModalFooter from '../ModalFooter';

const infoList = [
  'You can make edits to the new listing with Zillow Rental Manager.',
  'Publish this version of the listing and renters will see the updates right away.',
  'You can switch back to your feed listing whenever you like.',
];

type Props = {
  isMultifamily?: boolean;
  useCreateManualVersion?: boolean;
};

const TransferFeedListingModal = ({
  isMultifamily: isMultifamilyPassInProp = false,
  useCreateManualVersion = false,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));
  const { isMultifamily } = useSelector(
    (state) => ({
      isMultifamily: state?.properties?.currentProperty?.isMultifamily,
    }),
    shallowEqual,
  );

  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    const endpoint = useCreateManualVersion ? PropertiesActions.createManualVersion : ClaimActions.convert;

    setIsSubmitting(true);

    dispatch(endpoint(listingId))
      // @ts-expect-error
      .then((response: { listingAlias: string }) => {
        const manualListingAlias = response.listingAlias;

        if (manualListingAlias) {
          const finalizedListingPath = isMultifamilyPassInProp || isMultifamily ? 'listing' : 'create-listing';
          window.router.updateLocation(`${paths.propertiesPage}/${manualListingAlias}/${finalizedListingPath}`);
        } else {
          throw new Error('Missing response.listingAlias.');
        }
      })
      .catch(() => {
        setErrorMessage(ErrorMessageConstants.DEFAULT_ERROR);
        setIsSubmitting(false);
      });
  };

  return (
    <Modal
      header={
        <Heading level={1} textStyle="heading-xs">
          Automatically create a new version
        </Heading>
      }
      body={
        <>
          <DetailedIconMovingTruck />
          <Paragraph css={{ textStyle: 'heading-sm', marginBlock: '400' }}>Transfer your feed listing data</Paragraph>
          <List.Root>
            {infoList.map((item) => (
              <List.Item key={item}>{item}</List.Item>
            ))}
          </List.Root>
        </>
      }
      footer={
        <ModalFooter
          cancelText="Cancel"
          submitText="Create now"
          onSubmit={handleSubmit}
          errorMessage={errorMessage}
          hasError={errorMessage !== null}
          isSubmitting={isSubmitting}
        />
      }
      multiModalTriggerId="TransferFeedListingModal"
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default TransferFeedListingModal;
